import React from 'react';
import { Box, Typography, Tooltip, Stack, StackProps, SxProps } from '@mui/material';
import NoPrgramFound from '../../assets/no-program-found.svg';
import LoaderComponent from 'components/Loader';
import SplitNumber from './SplitNumber';
import { ArrowDownward, ArrowUpward } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { InfoTooltip } from './InfoTooltip';

type NumberWidgetProps = {
	color?: string;
	titleSx?: SxProps;
	bgColor?: string;
	reverseTrendColor?: boolean;
	isLoading: boolean;
	isInProgress?: boolean;
	title: any;
	titleKey?: string;
	titleFontSize?: string; 
	tooltipLabel: string;
	footer?: {
		footerLink: string;
		footerLabel: string;
	};
	isAnnualMetrics?: boolean;
	number?: number | string | string[] | number[] | null;
	units?: null | string | string[];
	pbiUnits?: null | string | string[];
	unitIcon?: null | string;
	unitIconColor?: null | string;
	change?: string | number;
	changeType?: 'increase' | 'decrease' | string;
	refreshersRate?: null | string;
	refreshersRateUnitIcon?: null | string;
	isCost?: boolean;
	minTitleHeight?: `${number}px`;
	hasTooltip?: boolean;
	handleEvent?: () => void;
	ratioNumber?: {
		number: number | string;
		totalNumber: number | string;
		growthRate: number | string;
	};
	totalRefreshers?: number;
	containerProps?: StackProps;
	selectedTitle?: string;
	sectorLabel?: string;
	className?: string;
	isHappySignal?: boolean;
	subText?: string;
	lostTime?: string;
	showChangePercentage?: boolean;
	isHappySignalTooltip?: boolean;
	disableHover?: boolean;
	valueFontSize?: any;
	valueFontWeight?: any;
	sameDecimalSize?: boolean;
	renderTooltip?: (tooltipLabel: string) => React.ReactNode;
	sectorChipBgColor?: string;
	sectorChipColor?: string;
};

const NumberWidget = ({
	color = 'text.main',
	titleSx,
	bgColor = 'primary.main',
	reverseTrendColor = false,
	isLoading = false,
	isInProgress = false,
	title = '',
	titleKey = '',
	titleFontSize = '',
	tooltipLabel = '',
	number = '',
	units = null,
	unitIcon = null,
	unitIconColor = null,
	change,
	changeType,
	refreshersRate = null,
	isAnnualMetrics = false,
	refreshersRateUnitIcon = null,
	footer,
	minTitleHeight = '38px',
	isCost = false,
	hasTooltip = true,
	handleEvent,
	ratioNumber,
	pbiUnits = null,
	totalRefreshers,
	containerProps,
	selectedTitle,
	sectorLabel = '',
	className,
	isHappySignal = false,
	isHappySignalTooltip = false,
	subText='',
	lostTime = '',
	showChangePercentage = false,
	disableHover = false,
	valueFontSize,
	valueFontWeight,
	sameDecimalSize = false,
	renderTooltip,
	sectorChipBgColor,
	sectorChipColor
}: Readonly<NumberWidgetProps>) => {
	const theme = useTheme();
	const colors = theme.palette;

	const formatPercentage = (value: string | number | null | undefined): string => {
		const numericValue = Number(value);
		if (!Number.isFinite(numericValue)) {
			return '0.00';
		}
		return numericValue.toFixed(2);
	};

	// Function to determine change color based on title and change type
	const getChangeColor = () => {
		if (change == 0) return colors.kpiTile?.text?.noChange;
		
		// Special logic for Inactive Users and Capacity Throttled - reverse the colors
		if (titleKey === 'Inactive Users' || titleKey === 'Throttled') {
			// For inactive users and capacity throttled: increase is bad (red), decrease is good (green)
			return changeType === 'increase' ? colors.kpiTile?.text?.changeUp : colors.kpiTile?.text?.changeDown;
		}
		
		// Default logic for all other KPIs: increase is good (green), decrease is bad (red)
		let color: string | undefined;

		if (!reverseTrendColor) {
			if (changeType === 'increase') {
				color = colors.kpiTile?.text?.changeDown;
			} else {
				color = colors.kpiTile?.text?.changeUp;
			}
		} else {
			if (changeType === 'decrease') {
				color = colors.kpiTile?.text?.changeDown;
			} else {
				color = colors.kpiTile?.text?.changeUp;
			}
		}

		return color;
	};

	const renderUnitIcon = (
		unitIcon?: string,
		ratioNumber?: {
			number: string | number;
			totalNumber: string | number;
			growthRate: string | number;
		},
		unitIconColor?: string | null
		) => {
		if (!unitIcon) return null;

		if (unitIcon === 'upArrow' || unitIcon === 'downArrow') {
			const ArrowComponent =
			unitIcon === 'upArrow' ? ArrowUpward : ArrowDownward;

			return (
			<Box display="flex" gap={0} alignItems="baseline" sx={{ ml: '10px' }}>
				{ratioNumber?.growthRate !== undefined && (
				<Typography variant="widgetUnit" sx={{ color: unitIconColor }}>
					{formatPercentage(ratioNumber.growthRate)}%
				</Typography>
				)}
				<ArrowComponent
				sx={{
					ml: '2px',
					color: unitIconColor,
					position: 'relative',
					top: '2px'
				}}
				/>
			</Box>
			);
		}

		return (
			<img
			src={unitIcon}
			style={{
				height: '24px',
				width: '24px',
				marginLeft: '10px'
			}}
			/>
		);
	};

	const getBorderBottom = (): string => {
		if (!selectedTitle) {
			return 'none';
		}

		if (selectedTitle !== titleKey) {
			return 'none';
		}

		if (
			['Software Cost', 'Hardware Cost', 'Labor Cost'].includes(
				selectedTitle
			)
		) {
			return '4px solid #009BD8';
		}

		// due to change in bg color of the application
		// we are changing the default border color from white to #009bd8
		return '4px solid #009bd8';
	};

	const renderNumberSection = () => {
		if (Array.isArray(number)) {
			return number.map((item, index) =>
				item !== '' && item !== null ? (
					<Stack
						direction="row"
						alignItems="baseline"
						key={`${item}-${index}`}
						ml={index !== 0 ? '10px' : 0}
					>
						<SplitNumber number={item} isCost={isCost} fontSize={valueFontSize} fontWeight={valueFontWeight} sameDecimalSize={sameDecimalSize} />
						{!!units && units[index] ? (
							<Typography
								variant={sameDecimalSize ? 'widgetValue' : 'widgetUnit'}
								sx={{
									ml: '10px',
									fontSize: sameDecimalSize
										? (valueFontSize ?? { xs: '38px', sm: '50px', md: '60px' })
										: (valueFontSize ?? { xs: '14px', sm: '24px', md: '32px' }),
									...(valueFontWeight && { fontWeight: valueFontWeight })
								}}
							>
								{units[index]}
							</Typography>
						) : (
							''
						)}{' '}
					</Stack>
				) : null
			);
		}

		if (number !== '' && number !== null) {
			return (
				<>
					<SplitNumber number={number} isCost={isCost} fontSize={valueFontSize} fontWeight={valueFontWeight} sameDecimalSize={sameDecimalSize} />
					{units && !Array.isArray(units) && (
						<Typography
							variant={sameDecimalSize ? 'widgetValue' : 'widgetUnit'}
							sx={{
								ml: '6px',
								fontSize: sameDecimalSize
									? (valueFontSize ?? { xs: '38px', sm: '50px', md: '60px' })
									: (valueFontSize ?? { xs: '14px', sm: '24px', md: '32px' }),
								...(valueFontWeight && { fontWeight: valueFontWeight })
							}}
						>
							{units}
						</Typography>
					)}
				</>
			);
		}

		if (number == null && units == 'hrs') {
			return (
				<Typography variant="bodyText">
					Insufficient Data
				</Typography>
			);
		}

		return null;
	};

	return (
		<Box
			display="flex"
			flexGrow={1}
			height="100%"
			position="relative"
			{...(handleEvent && { onClick: handleEvent, sx: { cursor: 'pointer' } })}
		>
			<Stack
				minHeight= "100px"
				p={2}
				bgcolor={bgColor}
				color={color}
				fontFamily="Montserrat"
				borderRadius="5px"
				flexGrow={1}
				{...containerProps}
				sx={{
				position: 'relative',
				flexDirection: 'row',
				justifyContent: 'space-between',
				borderBottom: getBorderBottom(),
				borderRadius: '5px',
				transition: 'all 0.3s ease-in-out',
				...(!disableHover && {
					'&:hover': {
						boxShadow: 4,
						transform: 'translateY(-2px)',
						backgroundColor: bgColor,
					},
				}),
				'&:hover .hover-content': {
					opacity: 1,
				},
				...containerProps?.sx,
				}}
			>
				<Stack sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', flex: '30%' }}>
					<Stack direction="row" gap={2} alignItems="start" marginTop = {isHappySignal ? "35px" : ''} minHeight={minTitleHeight}>
						<Typography variant="widgetTitle" sx={{ paddingRight: '16px', ...titleSx }}>
							{title}
						</Typography>
						{hasTooltip && !!tooltipLabel && (
							<Box
								className="hover-content"
								sx={{
									opacity: 1,
									transition: 'opacity 0.3s ease-in-out'
								}}
							>
								{renderTooltip ? (
									renderTooltip(tooltipLabel)
								) : (
									<InfoTooltip label={tooltipLabel} />
								)}
							</Box>
						)}
				</Stack>
				{sectorLabel != '' && (
					<Stack
						sx={{
							display: 'flow-root !important'
						}}
					>
						<Box
							bgcolor={sectorChipBgColor ?? "blue.darker"}
							sx={{
								...(sectorChipColor && { color: sectorChipColor }),
								borderRadius: '16px',
								width: 'auto',
								float: 'right',
								display: 'flex',
								marginTop: '-36px',
								height: 'auto',
								alignItems: 'center',
								padding: '5px'
							}}
						>
							<Typography
								variant="bodyText"
								sx={{ textAlign: 'center' }}
							>
								{sectorLabel}
							</Typography>
						</Box>
					</Stack>
				)}
				<Stack minHeight="43px" marginTop = {isHappySignal ? "50px" : ''} flexGrow={1} justifyContent="center">
					{!isLoading ? (
						<Stack color={color} direction="column" alignItems="start">
						<Stack direction="row" alignItems="baseline" flexWrap="wrap">
							{renderNumberSection()}
							{!!ratioNumber && (
								<Box display="flex" gap={0} alignItems="baseline" className={className}>
									<Typography variant="widgetValue">{ratioNumber.number}</Typography>
									{ratioNumber.totalNumber && ratioNumber.totalNumber !== '' && (
										<Typography variant="widgetRatioValue" ml={0} color="light.diff">
											/{ratioNumber.totalNumber}
										</Typography>
									)}
								</Box>
							)}
							{pbiUnits && (
								<Typography variant="widgetValue" ml={1}>
									{pbiUnits}
								</Typography>
							)}
							{totalRefreshers && (
								<Box display="flex" gap={0} alignItems="baseline">
									<Typography variant="breadcrumbs">{totalRefreshers}</Typography>
									<Box sx={{ ml: 1 }}>
										{refreshersRate && (
											<Typography variant="breadcrumbs" sx={{ color: unitIconColor }}>
												{refreshersRate}
											</Typography>
										)}
										{refreshersRateUnitIcon &&
											(refreshersRateUnitIcon === 'upArrow' ? (
												<ArrowUpward sx={{ color: unitIconColor, position: 'relative', top: '2px' }} />
											) : (
												<ArrowDownward sx={{ color: unitIconColor }} />
											))}
									</Box>
								</Box>
							)}
							{unitIcon && renderUnitIcon(unitIcon, ratioNumber, unitIconColor)}
						</Stack>
						</Stack>
					) : (
						<LoaderComponent />
					)}
				</Stack>
				{
					!isLoading && subText !== '' && (
							<Box sx={{ maxWidth: '250px' }}>
								 <Tooltip title={subText}>
										<Typography
											variant="widgetTitle"
											sx={{
											fontSize: '14px',
											overflow: 'hidden',
											whiteSpace: 'nowrap',
											textOverflow: 'ellipsis',
											color: color
											}}
										>
											{subText}
										</Typography>
								 </Tooltip>
							</Box>
					)
				}
				
				{isInProgress && (
					<Box position="absolute" right={15} bottom={10}>
						<Tooltip title="In Progress/Validation">
							<img
								src={NoPrgramFound}
								style={{
									height: '20px',
									width: '20px'
								}}
							/>
						</Tooltip>
					</Box>
				)}
					{showChangePercentage && (
						<Box display="flex" alignItems="center" mt={1} color={getChangeColor()}>
							{change != 0 && (changeType === 'increase' ? (
								<ArrowUpward fontSize="small" />
							) : (
								<ArrowDownward fontSize="small" />
							))}
							<Typography variant="bodyText" ml={0.5}>
							{`${formatPercentage(change ?? 0)}%`} vs previous {`${isAnnualMetrics ? 'year' : 'month'}`}

							</Typography>
						</Box>
					)}
			</Stack>

			{!!isHappySignal && 
			<Stack sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', flex: '20%' }}>
				<Stack direction="row" gap={2} alignItems="start" marginTop = {isHappySignal ? "35px" : ''} minHeight={minTitleHeight}>
					<Typography variant="widgetTitle">
						Lost Time
					</Typography>
				</Stack>
				<Stack minHeight="43px" marginTop = {isHappySignal ? "50px" : ''} flexGrow={1} justifyContent="center">
					<Stack color={color} direction="column" alignItems="start">
						<Stack direction="row" alignItems="baseline" flexWrap="wrap">
							<Typography variant="widgetTitle">{lostTime}</Typography>						
						</Stack>
					</Stack>
				</Stack>
			</Stack>}
			</Stack>
		</Box>
	);
};

export default NumberWidget;
